export type NotificationType =
  | 'mention'
  | 'comment_reply'
  | 'ownership_assigned'
  | 'ownership_removed'
  | 'comment_on_followed'
  | 'rock_status_changed'
  | 'metric_data_entered'
  | 'task_rescheduled'
  | 'entity_deleted'
  | 'milestone_completed'
  | 'milestone_rescheduled'
  | 'task_completed'
  | 'metric_off_track'
  | 'reaction'
  | 'task_due_soon'
  | 'rock_update_posted'
  | 'role_assignment'
  | 'stakeholder_added'
  | 'stakeholder_removed'
  | 'stakeholder_role_changed'
  | 'stakeholder_lead_assigned'
  | 'stakeholder_lead_removed'
  | 'billing_payment_failed'
  | 'billing_trial_ending'
  | 'billing_trial_ended'
  | 'billing_subscription_cancelled'
  | 'billing_seat_overage'
  | 'billing_seat_demoted'
  | 'team_break_glass'
  | 'scorecard_cloned'
  | 'insight_raised'
  | 'prep_submitted'
  | 'prep_complete'
  | 'connected_apps_setting_changed'
  | 'nudge'
  | 'meeting_tasks_assigned'
  | 'meeting_summary'

export type NotificationCategory =
  | 'assignments'
  | 'billing'
  | 'permissions'
  | 'integrations'
  | 'privacy'
  | 'sharing'
  | 'rocks'
  | 'metrics'
  | 'issues'
  | 'tasks'
  | 'comments'
  | 'mentions'
  | 'reactions'
  | 'insights'
  | 'meeting_prep'
  | 'nudges'

export interface CategoryPref {
  inApp: boolean
  email: boolean
}

export const DEFAULT_NOTIFICATION_PREFS: Record<NotificationCategory, CategoryPref> = {
  assignments: { inApp: true, email: false },
  billing: { inApp: true, email: true },
  permissions: { inApp: true, email: true },
  integrations: { inApp: true, email: true },
  privacy: { inApp: true, email: true },
  sharing: { inApp: true, email: false },
  rocks: { inApp: true, email: false },
  metrics: { inApp: true, email: false },
  issues: { inApp: true, email: false },
  tasks: { inApp: true, email: false },
  comments: { inApp: true, email: true },
  mentions: { inApp: true, email: true },
  reactions: { inApp: true, email: false },
  insights: { inApp: true, email: false },
  meeting_prep: { inApp: true, email: false },
  nudges: { inApp: true, email: true },
}

export const ALWAYS_IN_APP_CATEGORIES: NotificationCategory[] = ['nudges', 'billing', 'permissions', 'privacy']

export const ALWAYS_EMAIL_CATEGORIES: NotificationCategory[] = ['billing', 'permissions', 'privacy']

export function notificationCategory(type: string, entityType?: string | null): NotificationCategory {
  return ownCategory(type, entityType) ?? 'rocks'
}

export function hasOwnCategory(type: string): boolean {
  return ownCategory(type, null) !== null
}

function ownCategory(type: string, entityType?: string | null): NotificationCategory | null {
  switch (type) {
    case 'ownership_assigned':
    case 'ownership_removed':
    case 'meeting_tasks_assigned':
    case 'meeting_summary':
    case 'stakeholder_added':
    case 'stakeholder_removed':
    case 'stakeholder_role_changed':
    case 'stakeholder_lead_assigned':
    case 'stakeholder_lead_removed': return 'assignments'
    case 'rock_status_changed':
    case 'rock_update_posted':
    case 'milestone_completed':
    case 'milestone_rescheduled': return 'rocks'
    case 'metric_data_entered':
    case 'metric_off_track': return 'metrics'
    case 'task_completed':
    case 'task_rescheduled':
    case 'task_due_soon': return 'tasks'
    case 'comment_reply':
    case 'comment_on_followed': return 'comments'
    case 'mention': return 'mentions'
    case 'reaction': return 'reactions'
    case 'insight_raised': return 'insights'
    case 'prep_complete':
    case 'prep_submitted': return 'meeting_prep'
    case 'nudge': return 'nudges'
    case 'billing_payment_failed':
    case 'billing_trial_ending':
    case 'billing_trial_ended':
    case 'billing_seat_overage':
    case 'billing_subscription_cancelled': return 'billing'
    case 'role_assignment':
    case 'billing_seat_demoted': return 'permissions'
    case 'connected_apps_setting_changed': return 'integrations'
    case 'team_break_glass': return 'privacy'
    case 'scorecard_cloned': return 'sharing'
    case 'entity_deleted':
      if (entityType === 'metric') return 'metrics'
      if (entityType === 'task') return 'tasks'
      return 'rocks'
    default: return null
  }
}
